import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  parseExpenseCsv, mergeExpenseRows, monthLabelFromRows,
  computeTotals, parseStatementMonth, categorizeStore,
  merchantKey, blankRow, rowToCells, EXPENSE_HEADERS,
  learnedFromRows, buildLearnedMap, applyStoreToMerchant, sortRows, filterRows,
  knownStores, knownItems, toISODate, toMDY, uncategorizedCount,
  mergeExpenseState, purgeTrash,
  defaultSettle, ensureSettle, computeSettle, blankSettleLine,
  parseSettleCsv, settleToCsvRows, SETTLE_HEADERS,
} from './expenseUtils';
import { downloadCSV } from '../../utils/excelUtils';
import { useAuth } from '../../context/AuthContext';
import { subscribeExpense, saveExpense } from './expenseCloud';
import './ExpenseCalculator.css';

const SYNC_LABEL = { local: '', syncing: '☁ Saving…', synced: '☁ Synced', offline: '⚠ Offline — saved locally' };

const LS_MONTHS = 'exp_months';
const LS_ACTIVE = 'exp_active';
const LS_OVERRIDES = 'exp_overrides';
const LS_UPDATED = 'exp_updated';
const LS_TRASH = 'exp_trash';
const lsGet = (k, d) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const money = n => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = ms => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const newId = () => 'm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);

/** Load the months list, migrating from the old rows/history shape once. */
function loadMonths() {
  const months = lsGet(LS_MONTHS, null);
  if (months) return { months, activeId: lsGet(LS_ACTIVE, months[0]?.id || null) };
  // Migrate: old history folders + the old "current" rows become months.
  const migrated = (lsGet('exp_history', []) || []).map(h => ({ id: h.id, label: h.label, savedAt: h.savedAt, rows: h.rows }));
  let activeId = migrated[0]?.id || null;
  const oldRows = lsGet('exp_rows', []);
  if (Array.isArray(oldRows) && oldRows.length) {
    const cur = { id: newId(), label: lsGet('exp_month', '') || 'Current', savedAt: Date.now(), rows: oldRows };
    migrated.unshift(cur); activeId = cur.id;
  }
  return { months: migrated, activeId };
}

export default function ExpenseCalculator() {
  const [months, setMonths] = useState(() => loadMonths().months);
  const [activeId, setActiveId] = useState(() => loadMonths().activeId);
  const [overrides, setOverrides] = useState(() => lsGet(LS_OVERRIDES, {}));
  const [trash, setTrash] = useState(() => purgeTrash(lsGet(LS_TRASH, [])));
  const [sizeWarn, setSizeWarn] = useState(false);
  const [tab, setTab] = useState('current');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // ── In-app dialog (replaces window.prompt / window.confirm) ──
  // ask({type:'confirm'|'prompt', ...}) resolves true/false, or string/null.
  const [dialog, setDialog] = useState(null);
  const [dialogValue, setDialogValue] = useState('');
  function ask(opts) {
    return new Promise(resolve => {
      setDialogValue(opts.defaultValue || '');
      setDialog({ ...opts, resolve });
    });
  }
  function closeDialog(result) {
    if (dialog) dialog.resolve(result);
    setDialog(null);
  }
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = e => { if (e.key === 'Escape') closeDialog(dialog.type === 'prompt' ? null : false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const noticeTimer = useRef(null);
  const [query, setQuery] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const fileRef = useRef(null);
  const settleFileRef = useRef(null);

  // ── Cloud sync (owner-scoped), offline-safe ──
  const uid = (useAuth() || {}).user?.uid || null;
  const [sync, setSync] = useState('local');
  const remoteApplyRef = useRef(false);   // next state change came from the cloud, not an edit
  const firstSnapRef = useRef(true);
  const skipFirstSaveRef = useRef(true);  // don't treat the initial render as an edit
  const saveTimer = useRef(null);
  const stateRef = useRef();
  stateRef.current = { months, overrides, activeId, trash }; // always the latest, for async saves

  function applyCloud(data) {
    remoteApplyRef.current = true;
    setMonths(Array.isArray(data.months) ? data.months : []);
    setOverrides(data.overrides || {});
    setTrash(purgeTrash(data.trash));
    if (data.activeId) setActiveId(data.activeId);
    lsSet(LS_UPDATED, Number(data.updatedAtMs) || Date.now());
  }

  // Listen to the cloud doc. On the FIRST snapshot the local and cloud copies
  // are MERGED per-month (never adopt/overwrite wholesale), so neither side can
  // silently lose a month — deletions carry over via the trash tombstones.
  // Later snapshots are adopted directly: they're live edits from another device.
  useEffect(() => {
    if (!uid) return undefined;
    setSync('syncing');
    return subscribeExpense(uid, (data, exists) => {
      if (firstSnapRef.current) {
        firstSnapRef.current = false;
        const local = { ...stateRef.current, updatedAtMs: lsGet(LS_UPDATED, 0) };
        const cloud = exists ? data : null;
        const merged = mergeExpenseState(local, cloud);
        remoteApplyRef.current = true;
        setMonths(merged.months);
        setOverrides(merged.overrides);
        setTrash(merged.trash);
        setActiveId(merged.activeId);
        lsSet(LS_UPDATED, Math.max(lsGet(LS_UPDATED, 0), Number(cloud?.updatedAtMs) || 0));
        // Push the merged result up unless the cloud already matches it.
        const same = cloud && JSON.stringify([cloud.months || [], cloud.overrides || {}, purgeTrash(cloud.trash)])
          === JSON.stringify([merged.months, merged.overrides, merged.trash]);
        if (!same) saveExpense(uid, merged).catch(() => {});
      } else if (exists && data) {
        applyCloud(data); // change from another device
      }
      setSync('synced');
    });
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always cache locally (instant reload, offline).
  useEffect(() => {
    lsSet(LS_MONTHS, months); lsSet(LS_ACTIVE, activeId); lsSet(LS_OVERRIDES, overrides); lsSet(LS_TRASH, trash);
  }, [months, activeId, overrides, trash]);

  // On a genuine local edit: stamp the time and debounce a cloud write.
  useEffect(() => {
    if (skipFirstSaveRef.current) { skipFirstSaveRef.current = false; return undefined; }
    if (remoteApplyRef.current) { remoteApplyRef.current = false; return undefined; } // adopted remote, not an edit
    lsSet(LS_UPDATED, Date.now());
    // Firestore caps a document at ~1 MB — warn while there's still headroom.
    try { setSizeWarn(JSON.stringify(stateRef.current).length > 700_000); } catch {}
    if (!uid) return undefined; // offline / signed out — kept locally, pushed up on next sync
    setSync('syncing');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      saveExpense(uid, stateRef.current).then(() => setSync('synced')).catch(() => setSync('offline'));
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [months, overrides, activeId, trash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't lose the last edit: if a debounced save is still pending when the tab
  // closes (or the screen unmounts) — or connectivity returns — push it now.
  useEffect(() => {
    if (!uid) return undefined;
    const flush = () => {
      if (!saveTimer.current) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      saveExpense(uid, stateRef.current).then(() => setSync('synced')).catch(() => setSync('offline'));
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    window.addEventListener('online', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('online', flush);
      flush();
    };
  }, [uid]);

  const active = useMemo(() => months.find(m => m.id === activeId) || null, [months, activeId]);
  const rows = useMemo(() => active?.rows || [], [active]);

  const totals = useMemo(() => computeTotals(rows), [rows]);

  // Filtered + sorted rows. A row being EDITED stays pinned in place even if its
  // new value no longer matches the filter, so it can't vanish mid-keystroke
  // (and won't jump around as you type). It re-filters once you leave the row.
  const [editingId, setEditingId] = useState(null);
  const ordered = useMemo(() => sortRows(filterRows(rows, query, storeFilter), sortKey, sortDir), [rows, query, storeFilter, sortKey, sortDir]);
  const frozenIdsRef = useRef(null);
  useEffect(() => { if (!editingId) frozenIdsRef.current = ordered.map(r => r.id); }, [ordered, editingId]);
  const visible = useMemo(() => {
    if (!editingId || !frozenIdsRef.current) return ordered;
    const byId = new Map(rows.map(r => [r.id, r]));
    return frozenIdsRef.current.map(id => byId.get(id)).filter(Boolean);
  }, [ordered, editingId, rows]);
  const visibleTotals = useMemo(() => computeTotals(visible), [visible]);
  const storeOptions = useMemo(() => knownStores(rows, months, overrides), [rows, months, overrides]);
  const itemOptions = useMemo(() => knownItems(rows, months), [rows, months]);

  // Edits write straight into the open month (single source of truth → no dupes).
  function updateRows(fn) {
    setMonths(ms => ms.map(m => (m.id === activeId ? { ...m, rows: fn(m.rows), updatedAt: Date.now() } : m)));
  }

  // ── Settle-up (monthly profit) ──
  // Tracked business expenses feed the settle-up as the cost base, so revenue is
  // reconciled against whatever is actually entered on the expense table.
  const settle = useMemo(() => ensureSettle(active?.settle), [active]);
  const settleResult = useMemo(() => computeSettle(settle, totals.business), [settle, totals.business]);
  function updateSettle(fn) {
    setMonths(ms => ms.map(m => (m.id === activeId
      ? { ...m, settle: fn(ensureSettle(m.settle)), updatedAt: Date.now() }
      : m)));
  }
  const addSettleLine = kind => updateSettle(s => ({ ...s, [kind]: [...s[kind], blankSettleLine()] }));
  const changeSettleLine = (kind, id, field, value) =>
    updateSettle(s => ({ ...s, [kind]: s[kind].map(l => (l.id === id ? { ...l, [field]: value } : l)) }));
  const deleteSettleLine = (kind, id) =>
    updateSettle(s => ({ ...s, [kind]: s[kind].filter(l => l.id !== id) }));

  // Upload the "cash type / Amount" summary CSV straight into the settle-up:
  // positive rows → revenue, negative rows → deductions.
  function handleSettleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const parsed = parseSettleCsv(String(ev.target.result || ''));
        if (!parsed.revenue.length && !parsed.deductions.length) {
          setError('No rows found — expected a two-column “label, amount” CSV (e.g. “square, 24489”).');
          return;
        }
        const hasExisting = settleResult.gross > 0 || settleResult.deductions > 0;
        if (hasExisting) {
          const ok = await ask({
            type: 'confirm', title: 'Replace settle-up figures?',
            message: 'This month already has settle-up numbers. The uploaded CSV will replace them.',
            confirmLabel: 'Replace',
          });
          if (!ok) return;
        }
        updateSettle(() => ({ revenue: parsed.revenue, deductions: parsed.deductions }));
        setTab('settle');
      } catch (err) {
        setError('Could not read the file: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }
  function downloadSettle() {
    const fn = `settle-${(active?.label || 'export').replace(/\s+/g, '-').toLowerCase()}`;
    downloadCSV(SETTLE_HEADERS, settleToCsvRows(settle, totals.business), fn);
  }

  function showNotice(msg) {
    setNotice(msg);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => { setNotice(''); setUndo(null); }, 6000);
  }
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  /**
   * Import any expense CSV — a Chase activity export OR this app's own format.
   * All rows land in the open month (duplicates skipped, so re-uploading the
   * same statement is safe); with no month open, one is created automatically.
   */
  function importFile(file) {
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const learned = { ...buildLearnedMap(months, overrides) };
        const { rows: parsed, format } = parseExpenseCsv(String(ev.target.result || ''), learned);
        if (parsed.length === 0) { setError('No expense rows found — upload a Chase activity CSV or an exported expense CSV.'); return; }
        const fmt = format === 'chase' ? 'Chase CSV' : 'Expense CSV';
        if (active) {
          const { rows: mergedRows, added, skipped } = mergeExpenseRows(rows, parsed);
          updateRows(() => mergedRows);
          showNotice(`${fmt}: added ${added} transaction${added === 1 ? '' : 's'} to “${active.label}”${skipped ? ` · ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped` : ''}`);
        } else {
          let label = parseStatementMonth(file.name) || monthLabelFromRows(parsed);
          if (!label) {
            const typed = await ask({ type: 'prompt', title: 'Name this month', placeholder: 'e.g. July 2026', confirmLabel: 'Import' });
            label = (typed || '').trim() || 'Untitled month';
          }
          const month = { id: newId(), label, savedAt: Date.now(), rows: parsed, settle: defaultSettle() };
          setMonths(ms => [month, ...ms]);
          setActiveId(month.id);
          showNotice(`${fmt}: imported ${parsed.length} transaction${parsed.length === 1 ? '' : 's'} into “${label}”`);
        }
        setOverrides(o => ({ ...o, ...learnedFromRows(parsed) }));
        setQuery(''); setStoreFilter(''); setTab('current');
      } catch (err) {
        setError('Could not read the file: ' + err.message);
      }
    };
    reader.readAsText(file);
  }
  function handleFile(e) {
    importFile(e.target.files?.[0]);
    e.target.value = '';
  }

  // Drag & drop a CSV anywhere on the screen to import it.
  const dragHasFiles = e => Array.from(e.dataTransfer?.types || []).includes('Files');
  function onDragEnter(e) { if (!dragHasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragOver(true); }
  function onDragOver(e) { if (dragHasFiles(e)) e.preventDefault(); }
  function onDragLeave(e) { if (!dragHasFiles(e)) return; dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragOver(false); }
  function onDrop(e) {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0; setDragOver(false);
    importFile(e.dataTransfer.files?.[0]);
  }

  // ── Row editing ──
  function changeCell(id, field, value) { updateRows(rs => rs.map(r => (r.id === id ? { ...r, [field]: value } : r))); }
  function commitStore(id) {
    const row = rows.find(r => r.id === id);
    const key = merchantKey(row?.items || '');
    if (!key) return;
    updateRows(rs => applyStoreToMerchant(rs, row.items, row.store));
    setOverrides(o => ({ ...o, [key]: row.store }));
  }
  // Row deletes are undoable while the toast is showing.
  const [undo, setUndo] = useState(null); // { monthId, rows }
  function offerUndo(removed) {
    if (!removed.length) return;
    setUndo({ monthId: activeId, rows: removed });
    showNotice(`Deleted ${removed.length} row${removed.length === 1 ? '' : 's'}`);
  }
  function undoDelete() {
    const u = undo;
    setUndo(null); setNotice('');
    if (!u) return;
    setMonths(ms => ms.map(m => (m.id === u.monthId ? { ...m, rows: [...u.rows, ...m.rows], updatedAt: Date.now() } : m)));
  }
  function deleteRow(id) {
    const removed = rows.filter(r => r.id === id);
    updateRows(rs => rs.filter(r => r.id !== id));
    offerUndo(removed);
  }
  function deleteRows(ids) {
    const drop = new Set(ids);
    if (!drop.size) return;
    const removed = rows.filter(r => drop.has(r.id));
    updateRows(rs => rs.filter(r => !drop.has(r.id)));
    offerUndo(removed);
  }
  /** Move rows out of the open month into another (or a brand-new) month. */
  async function moveRows(ids, targetId) {
    const drop = new Set(ids);
    const moving = rows.filter(r => drop.has(r.id));
    if (!moving.length) return;
    let newMonth = null;
    if (targetId === '__new__') {
      const typed = await ask({
        type: 'prompt', title: 'Move to a new month',
        message: `${moving.length} row${moving.length === 1 ? '' : 's'} will move into the new month.`,
        defaultValue: monthLabelFromRows(moving) || '', placeholder: 'e.g. July 2026', confirmLabel: 'Move',
      });
      const label = (typed || '').trim();
      if (!label) return;
      newMonth = { id: newId(), label, savedAt: Date.now(), rows: [], settle: defaultSettle() };
      targetId = newMonth.id;
    }
    setMonths(ms => {
      const list = newMonth ? [newMonth, ...ms] : ms;
      if (!list.some(m => m.id === targetId)) return ms;
      return list.map(m => {
        if (m.id === activeId) return { ...m, rows: m.rows.filter(r => !drop.has(r.id)), updatedAt: Date.now() };
        if (m.id === targetId) return { ...m, rows: [...moving, ...m.rows], updatedAt: Date.now() };
        return m;
      });
    });
    const label = newMonth ? newMonth.label : (months.find(m => m.id === targetId)?.label || 'month');
    showNotice(`Moved ${moving.length} row${moving.length === 1 ? '' : 's'} to “${label}”`);
  }
  // Add from the pinned top form: auto-categorize the store, store the date as MM/DD/YYYY.
  function addExpense(d) {
    const items = (d.items || '').trim();
    if (!items && !d.cost) return;
    const learned = buildLearnedMap(months, overrides);
    const store = (d.store || '').trim() || categorizeStore(items, learned);
    updateRows(rs => [{ ...blankRow(), date: toMDY(d.date), items, store, cost: d.cost, personalCost: d.personalCost }, ...rs]);
  }
  function reCategorize() {
    const learned = { ...buildLearnedMap(months, overrides) };
    updateRows(rs => rs.map(r => (r.store ? r : { ...r, store: categorizeStore(r.items, learned) })));
  }
  function download(rs, label) {
    const fn = `expenses-${(label || active?.label || 'export').replace(/\s+/g, '-').toLowerCase()}`;
    downloadCSV(EXPENSE_HEADERS, (rs || rows).map(rowToCells), fn);
  }
  function onSort(key) {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(['cost', 'personalCost', 'date'].includes(key) ? 'desc' : 'asc'); }
  }
  const arrow = key => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // ── Month management ──
  async function newMonth() {
    const typed = await ask({ type: 'prompt', title: 'New month', placeholder: 'e.g. July 2026', confirmLabel: 'Create' });
    if (typed === null) return;
    const label = typed.trim() || 'Untitled month';
    const m = { id: newId(), label, savedAt: Date.now(), rows: [], settle: defaultSettle() };
    setMonths(ms => [m, ...ms]); setActiveId(m.id); setTab('current'); setQuery(''); setStoreFilter('');
  }
  async function renameMonth(id = activeId) {
    const m = months.find(x => x.id === id); if (!m) return;
    const label = await ask({ type: 'prompt', title: 'Rename month', defaultValue: m.label, confirmLabel: 'Rename' });
    if (label === null) return;
    setMonths(ms => ms.map(x => (x.id === id ? { ...x, label: label.trim() || x.label } : x)));
  }
  async function deleteMonth(id = activeId) {
    const m = months.find(x => x.id === id); if (!m) return;
    const ok = await ask({
      type: 'confirm', title: `Delete “${m.label}”?`,
      message: 'It moves to Recently deleted on the Months tab and stays restorable for 30 days.',
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    setTrash(ts => purgeTrash([{ ...m, deletedAt: Date.now() }, ...ts.filter(t => t.id !== id)]));
    setMonths(ms => {
      const next = ms.filter(x => x.id !== id);
      if (id === activeId) setActiveId(next[0]?.id || null);
      return next;
    });
  }
  function restoreMonth(id) {
    const t = trash.find(x => x.id === id); if (!t) return;
    const { deletedAt, ...m } = t;
    setTrash(ts => ts.filter(x => x.id !== id));
    // updatedAt > deletedAt, so the restore also survives the sync merge on other devices
    setMonths(ms => [{ ...m, updatedAt: Date.now() }, ...ms.filter(x => x.id !== id)]);
    setActiveId(id); setTab('current'); setQuery(''); setStoreFilter('');
  }
  async function destroyMonth(id) {
    const t = trash.find(x => x.id === id); if (!t) return;
    const ok = await ask({
      type: 'confirm', title: `Permanently delete “${t.label}”?`,
      message: 'This removes it from Recently deleted too. It cannot be undone.',
      confirmLabel: 'Delete forever', danger: true,
    });
    if (!ok) return;
    setTrash(ts => ts.filter(x => x.id !== id));
  }
  function openMonth(id) { setActiveId(id); setTab('current'); setQuery(''); setStoreFilter(''); }

  return (
    <div className="exp" onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {dragOver && (
        <div className="exp__drop">
          <div className="exp__drop-box">
            <span className="exp__drop-icon">⬆</span>
            <b>Drop your CSV to import</b>
            <span>{active ? `Everything lands in “${active.label}” — duplicates are skipped` : 'A new month will be created for it'}</span>
          </div>
        </div>
      )}
      {notice && (
        <div className="exp__toast" role="status">
          ✓ {notice}
          {undo && <button className="exp__toast-undo" onClick={undoDelete}>Undo</button>}
        </div>
      )}
      {dialog && (
        <div className="exp__modal-back" onMouseDown={() => closeDialog(dialog.type === 'prompt' ? null : false)}>
          <div className="exp__modal" role="dialog" aria-modal="true" aria-label={dialog.title} onMouseDown={e => e.stopPropagation()}>
            <h3 className="exp__modal-title">{dialog.title}</h3>
            {dialog.message && <p className="exp__modal-msg">{dialog.message}</p>}
            {dialog.type === 'prompt' && (
              <input
                autoFocus data-testid="exp-dialog-input" className="exp__modal-input"
                placeholder={dialog.placeholder || ''} value={dialogValue}
                onChange={e => setDialogValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') closeDialog(dialogValue); }}
              />
            )}
            <div className="exp__modal-actions">
              <button className="exp__btn" onClick={() => closeDialog(dialog.type === 'prompt' ? null : false)}>Cancel</button>
              <button autoFocus={dialog.type !== 'prompt'}
                className={`exp__btn ${dialog.danger ? 'exp__btn--danger-solid' : 'exp__btn--primary'}`}
                onClick={() => closeDialog(dialog.type === 'prompt' ? dialogValue : true)}>
                {dialog.confirmLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="exp__inner">
        <header className="exp__hero">
          <div>
            <h1 className="exp__title">💵 <span className="exp__title-grad">Expense Calculator</span></h1>
            {months.length > 0 ? (
              <div className="exp__monthrow">
                <select aria-label="Switch month" className="exp__monthsel" value={activeId || ''} onChange={e => openMonth(e.target.value)}>
                  {months.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
                {active && <span className="exp__sub">{rows.length} transaction{rows.length === 1 ? '' : 's'} · {money(totals.total)}</span>}
                {uid
                  ? (sync !== 'local' && <span className={`exp__synctag exp__synctag--${sync}`} title="Backed up to your account and synced across devices">{SYNC_LABEL[sync]}</span>)
                  : <span className="exp__synctag exp__synctag--syncing" title="Sign in to back up and sync across devices">💾 On this device</span>}
              </div>
            ) : (
              <p className="exp__sub">Import a CSV — Chase or a previously exported file — or create a month to start</p>
            )}
          </div>
          <div className="exp__actions">
            <input ref={fileRef} data-testid="exp-file-input" type="file" accept=".csv,text/csv" onChange={handleFile} hidden />
            <input ref={settleFileRef} type="file" accept=".csv,text/csv" onChange={handleSettleFile} hidden />
            <div className="exp__actions-row">
              <button className="exp__btn exp__btn--primary" onClick={() => fileRef.current?.click()}>⬆ Import CSV</button>
              <button className="exp__btn" onClick={newMonth}>＋ New month</button>
              {active && <>
                <button className="exp__btn" onClick={() => renameMonth()}>✎ Rename</button>
                <button className="exp__btn" onClick={() => download()} disabled={rows.length === 0}>⬇ Download</button>
                <button className="exp__btn exp__btn--danger" onClick={() => deleteMonth()}>🗑 Delete</button>
              </>}
            </div>
            <span className="exp__hint">Chase or exported format · or drag &amp; drop a CSV anywhere</span>
          </div>
        </header>

        {error && <div className="exp__error">⚠️ {error}</div>}
        {sizeWarn && (
          <div className="exp__warn">
            ⚠ Your expense data is getting close to the cloud storage limit. <b>Download</b> your oldest
            months as CSV (they re-import any time), then delete them to free up space.
          </div>
        )}
        <datalist id="exp-stores">{storeOptions.map(s => <option key={s} value={s} />)}</datalist>
        <datalist id="exp-items">{itemOptions.map(s => <option key={s} value={s} />)}</datalist>

        <div className="exp__tabs">
          <button className={`exp__tab${tab === 'current' ? ' on' : ''}`} onClick={() => setTab('current')}>📋 Current</button>
          <button className={`exp__tab${tab === 'settle' ? ' on' : ''}`} onClick={() => setTab('settle')}>💰 Settle-Up</button>
          <button className={`exp__tab${tab === 'history' ? ' on' : ''}`} onClick={() => setTab('history')}>🗂 Months ({months.length})</button>
        </div>

        {tab === 'current' ? (
          !active ? (
            <div className="exp__empty exp__empty--drop" role="button" tabIndex={0}
              onClick={() => fileRef.current?.click()}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}>
              <span className="exp__empty-icon">📄</span>
              <p>No month open. <b>Click to import a CSV</b> (Chase or exported format), drag &amp; drop one anywhere,<br />
                create a <b>New month</b>, or open one from <b>Months</b>.</p>
            </div>
          ) : (
            <CurrentView
              key={active.id}
              rows={rows} visible={visible} totals={totals} visibleTotals={visibleTotals}
              query={query} setQuery={setQuery} storeFilter={storeFilter} setStoreFilter={setStoreFilter}
              onSort={onSort} arrow={arrow} reCategorize={reCategorize}
              changeCell={changeCell} commitStore={commitStore} deleteRow={deleteRow} deleteRows={deleteRows} onAdd={addExpense}
              otherMonths={months.filter(m => m.id !== activeId).map(m => ({ id: m.id, label: m.label }))}
              onMoveRows={moveRows} confirmDialog={ask}
              setEditingId={setEditingId}
            />
          )
        ) : tab === 'settle' ? (
          !active ? (
            <div className="exp__empty">
              <span className="exp__empty-icon">💰</span>
              <p>No month open. Open or create a month first, then reconcile its revenue here.</p>
            </div>
          ) : (
            <SettleUp
              key={active.id}
              settle={settle} result={settleResult} business={totals.business}
              onAdd={addSettleLine} onChange={changeSettleLine} onDelete={deleteSettleLine}
              goToExpenses={() => setTab('current')}
              onUpload={() => settleFileRef.current?.click()} onDownload={downloadSettle}
            />
          )
        ) : (
          <Months months={months} activeId={activeId} trash={trash}
            onOpen={openMonth} onRename={renameMonth} onDelete={deleteMonth}
            onDownload={m => download(m.rows, m.label)} onNew={newMonth}
            onRestore={restoreMonth} onDestroy={destroyMonth} />
        )}
      </div>
    </div>
  );
}

// ── Editor for the open month ──
function CurrentView({ rows, visible, totals, visibleTotals, query, setQuery, storeFilter, setStoreFilter, onSort, arrow, reCategorize, changeCell, commitStore, deleteRow, deleteRows, onAdd, otherMonths, onMoveRows, confirmDialog, setEditingId }) {
  const needsReview = uncategorizedCount(rows);
  const [draft, setDraft] = useState({ date: '', items: '', store: '', cost: '', personalCost: '' });

  // ── Multi-row selection (bulk delete) ──
  const [selected, setSelected] = useState(() => new Set());
  // Only count rows that still exist (a row may have been deleted individually).
  const selectedIds = useMemo(() => rows.filter(r => selected.has(r.id)).map(r => r.id), [rows, selected]);
  const visibleSelected = useMemo(() => visible.filter(r => selected.has(r.id)), [visible, selected]);
  const allVisibleSelected = visible.length > 0 && visibleSelected.length === visible.length;
  const headCheckRef = useRef(null);
  useEffect(() => {
    if (headCheckRef.current) headCheckRef.current.indeterminate = visibleSelected.length > 0 && !allVisibleSelected;
  }, [visibleSelected.length, allVisibleSelected]);
  function toggleRow(id) {
    setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAllVisible() {
    setSelected(s => {
      const n = new Set(s);
      if (allVisibleSelected) visible.forEach(r => n.delete(r.id));
      else visible.forEach(r => n.add(r.id));
      return n;
    });
  }
  async function deleteSelected() {
    if (!selectedIds.length) return;
    const ok = await confirmDialog({
      type: 'confirm', title: `Delete ${selectedIds.length} selected row${selectedIds.length === 1 ? '' : 's'}?`,
      message: 'You can undo right after from the toast.',
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    deleteRows(selectedIds);
    setSelected(new Set());
  }
  const setD = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const canAdd = (draft.items || '').trim() || draft.cost;
  function add() {
    if (!canAdd) return;
    onAdd(draft);
    setDraft(d => ({ ...d, items: '', store: '', cost: '', personalCost: '' })); // keep date for fast entry
  }
  const onEnter = e => { if (e.key === 'Enter') add(); };

  return (
    <>
      <div className="exp__cards">
        <div className="exp__card"><span className="exp__card-lbl">Total spend</span><span className="exp__card-val">{money(totals.total)}</span></div>
        <div className="exp__card"><span className="exp__card-lbl">Personal</span><span className="exp__card-val exp__card-val--personal">{money(totals.personal)}</span></div>
        <div className="exp__card exp__card--biz"><span className="exp__card-lbl">Business</span><span className="exp__card-val exp__card-val--business">{money(totals.business)}</span></div>
        <div className="exp__card"><span className="exp__card-lbl">Transactions</span><span className="exp__card-val">{totals.count}</span></div>
      </div>

      {needsReview > 0 && (
        <button className={`exp__review${storeFilter === 'uncategorized' ? ' on' : ''}`}
          onClick={() => setStoreFilter(storeFilter === 'uncategorized' ? '' : 'uncategorized')}>
          ⚠ {needsReview} transaction{needsReview === 1 ? ' needs' : 's need'} a store — {storeFilter === 'uncategorized' ? 'showing them' : 'click to review'}
        </button>
      )}

      {totals.byStore.length > 0 && (
        <div className="exp__breakdown">
          <span className="exp__bd-lbl">By store {storeFilter && <button className="exp__mini" onClick={() => setStoreFilter('')}>✕ clear filter</button>}</span>
          <div className="exp__chips">
            {totals.byStore.map(s => (
              <button key={s.store} className={`exp__chip${storeFilter === s.store ? ' exp__chip--on' : ''}`}
                onClick={() => setStoreFilter(storeFilter === s.store ? '' : s.store)}>
                {s.store} <b>{money(s.amount)}</b>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pinned add-expense bar — always visible, so no scrolling to add */}
      <div className="exp__addbar">
        <input type="date" className="exp__add-in" value={draft.date} onChange={e => setD('date', e.target.value)} />
        <input className="exp__add-in exp__add-in--grow" list="exp-items" placeholder="Item / merchant" value={draft.items} onChange={e => setD('items', e.target.value)} onKeyDown={onEnter} />
        <input className="exp__add-in" list="exp-stores" placeholder="Store (auto)" value={draft.store} onChange={e => setD('store', e.target.value)} onKeyDown={onEnter} />
        <input className="exp__add-in exp__add-in--num" type="number" step="0.01" placeholder="Cost" value={draft.cost} onChange={e => setD('cost', e.target.value)} onKeyDown={onEnter} />
        <input className="exp__add-in exp__add-in--num" type="number" step="0.01" placeholder="Personal" value={draft.personalCost} onChange={e => setD('personalCost', e.target.value)} onKeyDown={onEnter} />
        <button className="exp__btn exp__btn--primary exp__btn--sm" onClick={add} disabled={!canAdd}>＋ Add</button>
      </div>

      {rows.length === 0 ? (
        <div className="exp__empty"><span className="exp__empty-icon">📄</span><p>This month is empty — add an expense above, or drop a CSV (Chase or exported) anywhere on this screen.</p></div>
      ) : (
        <>
          <div className="exp__toolbar">
            <input className="exp__search" placeholder="🔍 Search date, item, or store…" value={query} onChange={e => setQuery(e.target.value)} />
            {(query || storeFilter) && <button className="exp__btn exp__btn--sm" onClick={() => { setQuery(''); setStoreFilter(''); }}>Clear filters</button>}
            <span className="exp__count">Showing {visible.length} of {rows.length}</span>
          </div>
          {selectedIds.length > 0 && (
            <div className="exp__bulk">
              <span className="exp__bulk-count">{selectedIds.length} row{selectedIds.length === 1 ? '' : 's'} selected</span>
              <select className="exp__bulk-move" aria-label="Move selected rows to month" value=""
                onChange={e => { const v = e.target.value; if (v) { onMoveRows(selectedIds, v); setSelected(new Set()); } }}>
                <option value="">↪ Move to…</option>
                {otherMonths.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                <option value="__new__">＋ New month…</option>
              </select>
              <button className="exp__btn exp__btn--sm exp__btn--danger" onClick={deleteSelected}>🗑 Delete selected</button>
              <button className="exp__btn exp__btn--sm" onClick={() => setSelected(new Set())}>Clear selection</button>
            </div>
          )}
          <div className="exp__table-wrap">
            <table className="exp__table">
              <thead>
                <tr>
                  <th style={{ width: 36 }} className="exp__checkcol">
                    <input ref={headCheckRef} type="checkbox" className="exp__check" aria-label="Select all visible rows"
                      checked={allVisibleSelected} onChange={toggleAllVisible} />
                  </th>
                  <th style={{ width: 120 }} className="exp__sortable" onClick={() => onSort('date')}>Date{arrow('date')}</th>
                  <th className="exp__sortable" onClick={() => onSort('items')}>Items{arrow('items')}</th>
                  <th style={{ width: 170 }} className="exp__sortable" onClick={() => onSort('store')}>
                    Store{arrow('store')} {rows.some(r => !r.store) && <button className="exp__mini" onClick={ev => { ev.stopPropagation(); reCategorize(); }} title="Auto-fill blank stores">auto</button>}
                  </th>
                  <th style={{ width: 110 }} className="exp__num exp__sortable" onClick={() => onSort('cost')}>Cost{arrow('cost')}</th>
                  <th style={{ width: 130 }} className="exp__num exp__sortable" onClick={() => onSort('personalCost')}>Personal Cost{arrow('personalCost')}</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map(r => (
                  <tr key={r.id}
                    className={`${(r.store || '').trim() ? '' : 'exp__row--review'}${selected.has(r.id) ? ' exp__row--selected' : ''}`}
                    onFocus={() => setEditingId(r.id)}
                    onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setEditingId(null); }}>

                    <td className="exp__checkcol">
                      <input type="checkbox" className="exp__check" aria-label="Select row"
                        checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} />
                    </td>
                    <td><input className="exp__in" type="date" aria-label="Date" value={toISODate(r.date)} onChange={e => changeCell(r.id, 'date', toMDY(e.target.value))} /></td>
                    <td><input className="exp__in" list="exp-items" aria-label="Item" value={r.items} onChange={e => changeCell(r.id, 'items', e.target.value)} /></td>
                    <td><input className="exp__in" list="exp-stores" aria-label="Store" value={r.store} placeholder="—" onChange={e => changeCell(r.id, 'store', e.target.value)} onBlur={() => commitStore(r.id)} /></td>
                    <td className="exp__num"><input className="exp__in exp__in--num" type="number" step="0.01" aria-label="Cost" value={r.cost} onChange={e => changeCell(r.id, 'cost', e.target.value)} /></td>
                    <td className="exp__num"><input className="exp__in exp__in--num" type="number" step="0.01" aria-label="Personal cost" value={r.personalCost} placeholder="0" onChange={e => changeCell(r.id, 'personalCost', e.target.value)} /></td>
                    <td><button className="exp__del" onClick={() => deleteRow(r.id)} title="Delete row" aria-label="Delete row">✕</button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{visible.length === rows.length ? `Total (${rows.length})` : `Filtered total (${visible.length})`}</td>
                  <td className="exp__num"><b>{money(visibleTotals.total)}</b></td>
                  <td className="exp__num"><b>{money(visibleTotals.personal)}</b></td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </>
  );
}

// ── Settle-Up: reconcile revenue against costs → monthly net profit ──
function SettleUp({ settle, result, business, onAdd, onChange, onDelete, goToExpenses, onUpload, onDownload }) {
  const { gross, tracked, deductions, costs, net, margin, byChannel } = result;
  const profit = net >= 0;
  const max = byChannel.length ? byChannel[0].amount : 0;
  const doubleCount = tracked > 0 && settle.deductions.some(d => /expense/i.test(d.name || ''));

  return (
    <>
      <div className="exp__settle-bar">
        <div className="exp__settle-bar-info">
          Reconcile this month’s revenue against costs to get net profit — type it in, or upload your
          <b> cash&nbsp;type / Amount </b> sheet.
        </div>
        <div className="exp__settle-bar-actions">
          <button className="exp__btn exp__btn--sm" onClick={onDownload}>⬇ Export CSV</button>
          <button className="exp__btn exp__btn--primary exp__btn--sm" onClick={onUpload}>⬆ Upload table CSV</button>
        </div>
      </div>

      <details className="exp__fmt">
        <summary>CSV format &amp; example</summary>
        <p>Two columns: <b>label, amount</b>. Positive = revenue, negative = expense / deduction. Blank and “net/total” rows are ignored.</p>
        <pre>{`cash type,Amount
square,24489
cash and catering,3533
uber eats,963
doordash,1766
grubhub,85

expenses,-26049
cash entered,-203
city tax,-244
state tax,-2204`}</pre>
      </details>

      {doubleCount && (
        <div className="exp__warn">
          ⚠ This month has <b>tracked expenses ({money(tracked)})</b> from the expense table <i>and</i> an
          “expenses” deduction line — they may double-count. Remove one to keep net profit accurate.
        </div>
      )}

      <div className="exp__cards exp__cards--settle">
        <div className="exp__card"><span className="exp__card-lbl">Gross revenue</span><span className="exp__card-val exp__card-val--business">{money(gross)}</span></div>
        <div className="exp__card"><span className="exp__card-lbl">Total costs</span><span className="exp__card-val exp__card-val--cost">{money(costs)}</span></div>
        <div className={`exp__card exp__card--net exp__card--net-${profit ? 'up' : 'down'}`}>
          <span className="exp__card-lbl">Net profit — settled</span>
          <span className={`exp__card-val exp__card-val--${profit ? 'profit' : 'loss'}`}>{money(net)}</span>
        </div>
        <div className="exp__card"><span className="exp__card-lbl">Profit margin</span><span className="exp__card-val">{margin.toFixed(1)}%</span></div>
      </div>

      <div className="exp__settle-grid">
        {/* Revenue channels */}
        <div className="exp__panel">
          <div className="exp__panel-head">
            <span className="exp__panel-title"><span className="exp__dot exp__dot--rev" />Revenue channels</span>
            <span className="exp__panel-tot exp__panel-tot--rev">{money(gross)}</span>
          </div>
          <div className="exp__ledger">
            {settle.revenue.map(l => (
              <div className="exp__lrow" key={l.id}>
                <input className="exp__lname" list="exp-rev-names" placeholder="Channel name"
                  value={l.name} onChange={e => onChange('revenue', l.id, 'name', e.target.value)} />
                <div className="exp__lamt-wrap">
                  <span className="exp__lamt-cur">$</span>
                  <input className="exp__lamt" type="number" step="0.01" placeholder="0.00"
                    value={l.amount} onChange={e => onChange('revenue', l.id, 'amount', e.target.value)} />
                </div>
                <button className="exp__del" title="Remove" aria-label="Remove channel" onClick={() => onDelete('revenue', l.id)}>✕</button>
              </div>
            ))}
          </div>
          <button className="exp__addline" onClick={() => onAdd('revenue')}>＋ add revenue channel</button>
        </div>

        {/* Costs */}
        <div className="exp__panel">
          <div className="exp__panel-head">
            <span className="exp__panel-title"><span className="exp__dot exp__dot--cost" />Costs &amp; deductions</span>
            <span className="exp__panel-tot exp__panel-tot--cost">{money(costs)}</span>
          </div>
          <div className="exp__ledger">
            {/* Auto line: pulled live from the expense table */}
            <button className="exp__lrow exp__lrow--auto" onClick={goToExpenses} title="Edit on the Current tab">
              <span className="exp__lname exp__lname--auto">
                Tracked business expenses <span className="exp__autotag">from expense table</span>
              </span>
              <span className="exp__lamt exp__lamt--auto">{money(tracked)}</span>
              <span className="exp__del" aria-hidden>↗</span>
            </button>
            {settle.deductions.map(l => (
              <div className="exp__lrow" key={l.id}>
                <input className="exp__lname" placeholder="Deduction (tax, cash…)"
                  value={l.name} onChange={e => onChange('deductions', l.id, 'name', e.target.value)} />
                <div className="exp__lamt-wrap">
                  <span className="exp__lamt-cur">$</span>
                  <input className="exp__lamt" type="number" step="0.01" placeholder="0.00"
                    value={l.amount} onChange={e => onChange('deductions', l.id, 'amount', e.target.value)} />
                </div>
                <button className="exp__del" title="Remove" aria-label="Remove deduction" onClick={() => onDelete('deductions', l.id)}>✕</button>
              </div>
            ))}
          </div>
          <button className="exp__addline" onClick={() => onAdd('deductions')}>＋ add deduction</button>
          <div className="exp__settle-sub">
            Tracked expenses {money(tracked)} + other deductions {money(deductions)} = <b>{money(costs)}</b>
          </div>
        </div>
      </div>

      <datalist id="exp-rev-names">
        {['Square', 'Cash & catering', 'Uber Eats', 'DoorDash', 'Grubhub', 'Catering', 'Toast', 'Clover'].map(s => <option key={s} value={s} />)}
      </datalist>

      {/* Revenue mix */}
      {byChannel.length > 0 ? (
        <div className="exp__mix">
          <span className="exp__bd-lbl">Revenue mix</span>
          {byChannel.map(c => (
            <div className="exp__mixrow" key={c.name}>
              <span className="exp__mixname">{c.name}</span>
              <span className="exp__mixbar"><span className="exp__mixfill" style={{ width: `${max ? (c.amount / max) * 100 : 0}%` }} /></span>
              <span className="exp__mixval">{money(c.amount)}<span className="exp__mixpct">{gross ? ((c.amount / gross) * 100).toFixed(0) : 0}%</span></span>
            </div>
          ))}
        </div>
      ) : (
        <div className="exp__settle-note">Enter each channel's payout above to see your revenue mix and net profit.</div>
      )}

      <div className="exp__settle-note">
        <b>How this settles:</b> net profit = gross revenue − tracked business expenses − deductions.
        The tracked-expenses line updates automatically from what you enter on the <button className="exp__link" onClick={goToExpenses}>Current</button> tab.
      </div>
    </>
  );
}

// ── Months: every saved month as a folder (+ a 30-day Recently deleted bin) ──
function Months({ months, activeId, trash, onOpen, onRename, onDelete, onDownload, onNew, onRestore, onDestroy }) {
  const bin = trash || [];
  if (months.length === 0 && bin.length === 0) {
    return <div className="exp__empty"><span className="exp__empty-icon">🗂</span><p>No months yet.</p><button className="exp__btn exp__btn--primary" onClick={onNew} style={{ marginTop: 12 }}>＋ New month</button></div>;
  }
  return (
    <>
    <div className="exp__folders">
      {months.map(m => {
        const t = computeTotals(m.rows);
        const s = computeSettle(m.settle, t.business);
        const isActive = m.id === activeId;
        return (
          <div key={m.id} className={`exp__folder${isActive ? ' exp__folder--active' : ''}`}>
            <button className="exp__folder-open" onClick={() => onOpen(m.id)}>
              <span className="exp__folder-icon">🗂</span>
              <span className="exp__folder-name">{m.label}{isActive && <span className="exp__folder-badge">editing</span>}</span>
              <span className="exp__folder-meta">{t.count} txns · {money(t.total)}</span>
              {s.gross > 0 && (
                <span className={`exp__folder-profit exp__folder-profit--${s.net >= 0 ? 'up' : 'down'}`}>
                  net {money(s.net)} · {s.margin.toFixed(0)}% margin
                </span>
              )}
              <span className="exp__folder-date">saved {fmtDate(m.savedAt)}</span>
            </button>
            <div className="exp__folder-actions">
              <button className="exp__btn exp__btn--sm" onClick={() => onOpen(m.id)}>Open</button>
              <button className="exp__btn exp__btn--sm" onClick={() => onRename(m.id)}>✎</button>
              <button className="exp__btn exp__btn--sm" onClick={() => onDownload(m)}>⬇</button>
              <button className="exp__btn exp__btn--sm exp__btn--danger" onClick={() => onDelete(m.id)}>✕</button>
            </div>
          </div>
        );
      })}
    </div>
    {bin.length > 0 && (
      <div className="exp__trash">
        <span className="exp__bd-lbl">🗑 Recently deleted — restorable for 30 days</span>
        {bin.map(t => {
          const tt = computeTotals(t.rows);
          return (
            <div key={t.id} className="exp__trash-row">
              <span className="exp__trash-name">{t.label}</span>
              <span className="exp__trash-meta">{tt.count} txns · {money(tt.total)} · deleted {fmtDate(t.deletedAt)}</span>
              <button className="exp__btn exp__btn--sm" onClick={() => onRestore(t.id)}>↩ Restore</button>
              <button className="exp__btn exp__btn--sm exp__btn--danger" onClick={() => onDestroy(t.id)}>Delete forever</button>
            </div>
          );
        })}
      </div>
    )}
    </>
  );
}
